import { useMemo, useRef, useState } from 'react'
import { Loader2, Search, X } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

interface MultiSelectOption {
  value: string
  label: string
}

interface MultiSelectComboboxProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  isLoading?: boolean
}

export function MultiSelectCombobox({
  options,
  value,
  onChange,
  placeholder = 'Search to find or add a team member',
  className,
  disabled = false,
  isLoading = false,
}: MultiSelectComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedOptions = useMemo(
    () => options.filter((option) => value.includes(option.value)),
    [options, value]
  )

  const filteredOptions = useMemo(() => {
    if (!query) return options
    return options.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    )
  }, [options, query])

  const handleToggleOption = (option: MultiSelectOption) => {
    if (disabled) return
    const isSelected = value.includes(option.value)
    const next = isSelected
      ? value.filter((v) => v !== option.value)
      : [...value, option.value]
    onChange(next)
    setQuery('')
    inputRef.current?.focus()
  }

  const handleRemoveOption = (e: React.MouseEvent, optionValue: string) => {
    e.stopPropagation()
    if (disabled) return
    const next = value.filter((v) => v !== optionValue)
    onChange(next)
  }

  const handleInputClick = () => {
    if (!disabled) {
      setOpen(true)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      const next = value.slice(0, -1)
      onChange(next)
    }
    if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          onClick={handleInputClick}
          className={cn(
            'flex min-h-10 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border border-primary/20 bg-background px-3 py-2',
            disabled && 'cursor-not-allowed opacity-50',
            open && 'ring-1 ring-ring',
            className
          )}
        >
          {/* Search icon */}
          <Search className="size-4 shrink-0 text-primary/70" />

          {/* Selected chips */}
          {selectedOptions.map((option) => (
            <Badge key={option.value} className="gap-1 pr-1 text-xs">
              {option.label}
              <button
                type="button"
                onClick={(e) => handleRemoveOption(e, option.value)}
                className="rounded-full hover:bg-primary/10"
                aria-label={`Remove ${option.label}`}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}

          {/* Inline search input */}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              if (!open) setOpen(true)
            }}
            onKeyDown={handleKeyDown}
            onClick={(e) => {
              e.stopPropagation()
              if (!disabled && !open) setOpen(true)
            }}
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            disabled={disabled}
            className="min-w-20 flex-1 bg-transparent font-inter text-xs text-primary outline-none placeholder:text-primary/70"
          />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <Command shouldFilter={false}>
          <CommandList>
            <CommandEmpty className="py-3 text-center font-inter text-xs">
              {isLoading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="size-3 animate-spin" />
                  <span>Loading...</span>
                </div>
              ) : (
                'No results found'
              )}
            </CommandEmpty>
            {filteredOptions.length > 0 && (
              <CommandGroup>
                <div className="px-2 py-1.5 text-[10px] text-primary/70">
                  Suggested
                </div>
                {filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value)
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      onSelect={() => handleToggleOption(option)}
                      className="cursor-pointer font-inter"
                    >
                      <Checkbox checked={isSelected} className="mr-2" />
                      <span className="text-xs">{option.label}</span>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
